import React, { useState, useRef, useEffect } from 'react'
import { Send, Paperclip, Mic } from 'lucide-react'
import { MessageBubble } from './MessageBubble'
import { ChatMessage, FileData } from '../types/chat'
import { ConversationMessage } from '../types/conversationHistory'

interface ChatInterfaceProps {
  messages: ChatMessage[]
  isLoading: boolean
  isDebugMode?: boolean
  onSendMessage: (content: string, type?: 'text' | 'audio' | 'document' | 'image', fileData?: FileData) => void
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isLoading,
  isDebugMode = false,
  onSendMessage
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isRecording, setIsRecording] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (inputValue.trim()) {
      onSendMessage(inputValue.trim())
      setInputValue('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const fileData: FileData = {
      fileName: file.name,
      fileSize: file.size,
      fileType: file.type
    }

    let messageType: 'document' | 'image' = 'document'
    if (file.type.startsWith('image/')) {
      messageType = 'image'
    }

    const reader = new FileReader()
    reader.onload = () => {
      fileData.fileUrl = reader.result as string
      onSendMessage(`📎 ${file.name}`, messageType, fileData)
    }
    reader.readAsDataURL(file)

    e.target.value = ''
  }

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      mediaRecorderRef.current = new MediaRecorder(stream)
      audioChunksRef.current = []

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data)
      }

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' })
        const audioUrl = URL.createObjectURL(audioBlob)
        
        const fileData: FileData = {
          fileName: `audio_${Date.now()}.wav`,
          fileSize: audioBlob.size,
          fileType: 'audio/wav',
          fileUrl: audioUrl
        }

        onSendMessage('🎤 Mensaje de audio', 'audio', fileData)
        stream.getTracks().forEach(track => track.stop())
      }

      mediaRecorderRef.current.start()
      setIsRecording(true)
    } catch (error) {
      console.error('Error accessing microphone:', error)
      alert('No se pudo acceder al micrófono')
    }
  }

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording()
    } else {
      startRecording()
    }
  }

  const convertToConversationMessage = (msg: ChatMessage): ConversationMessage => {
    let messageType: 'user' | 'bot' | 'tool' | 'summary' | 'system' = 'bot'
    
    if (msg.sender === 'user') {
      messageType = 'user'
    } else if (msg.sender === 'bot') {
      messageType = 'bot'
    } else if (msg.sender === 'system') {
      if (msg.debugInfo?.type === 'tool_call') {
        messageType = 'tool'
      } else if (msg.debugInfo?.type === 'system') {
        messageType = 'system'
      } else if (msg.content.includes('[SUMMARY]')) {
        messageType = 'summary'
      } else {
        messageType = 'system'
      }
    }

    return {
      id: parseInt(msg.id) || Date.now(),
      type: messageType,
      content: msg.content,
      metadata: msg.debugInfo?.toolName ? {
        toolName: msg.debugInfo.toolName,
        toolArgs: null,
        toolResult: null
      } : undefined,
      timestamp: msg.timestamp
    }
  }

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <div className="chat-avatar">
          🤖
        </div>
        <div className="chat-info">
          <h3>Asistente de Viajes</h3>
          <p>
            Especialista en viajes de boda
            {isDebugMode && <span> • DEBUG MODE</span>}
          </p>
        </div>
      </div>

      <div className="messages-container">
        {messages.length === 0 && (
          <div className="welcome-message">
            ¡Hola! Soy tu asistente especializado en viajes de boda. 
            ¿En qué puedo ayudarte hoy?
          </div>
        )}
        
        {messages.map((message) => {
          const conversationMessage = convertToConversationMessage(message)
          
          return (
            <MessageBubble 
              key={message.id} 
              message={conversationMessage} 
              isHistorical={message.isHistorical || false}
            />
          )
        })}
        
        {isLoading && (
          <div className="typing-indicator">
            <span>El asistente está escribiendo</span>
            <div className="typing-dots">
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
              <div className="typing-dot"></div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="input-area">
        <button
          type="button"
          className="attach-button"
          onClick={() => fileInputRef.current?.click()}
          title="Adjuntar archivo"
        >
          <Paperclip size={18} />
        </button>

        <textarea
          ref={inputRef}
          className="message-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Escribe tu mensaje..."
          rows={1}
        />

        {isRecording ? (
          <div className="recording-indicator">
            <div className="recording-dot"></div>
            <span>Grabando...</span>
          </div>
        ) : (
          <button
            type="button"
            className="audio-button"
            onClick={toggleRecording}
            title="Grabar audio"
          >
            <Mic size={18} />
          </button>
        )}

        <button 
          type="button"
          onClick={handleSubmit}
          disabled={!inputValue.trim()}
          className="send-button"
        >
          <Send size={18} />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="image/*,application/pdf,.doc,.docx,.txt"
        />
      </div>
    </div>
  )
}

export default ChatInterface
